// Overview: one card per section, comparing the selected games inside it.
// Rows show each game's average (bar + value + overflow flag); a mini chart overlays the games
// lined up by game start. Cards are sorted worst first; selecting one shows its full charts below.
//
// PDF report: it finds section tabs by looking for <button>s whose text is exactly "Section N" and
// treats the one with a white background as active. Each card's title button follows that contract,
// so keep its text exactly "Section {id}" and the active one white.
import React, { useState } from 'react';
import moment from 'moment';
import Icon from '../../../components/atoms/Icon';
import { getQueueTimeStatus, getPeopleCountStatus, STATUS_COLORS, QUEUE_TIME_GOOD_MAX } from '../concessionTheme';
import { formatDuration } from '../live/liveMetrics';
import { OverflowPill, StatusPill } from '../live/LiveComponents';
import { Slot } from '../extensions/Slot';
import '../concession.css';

const COUNT_SECTION_LOW = 15;
const COUNT_SECTION_HIGH = 30;

const offsetLabel = (minutes) => {
  const m = Math.round(minutes);
  if (m === 0) return 'Game start';
  const abs = Math.abs(m);
  const text = abs >= 60 ? `${Math.floor(abs / 60)}h${abs % 60 ? ` ${abs % 60}m` : ''}` : `${abs}m`;
  return m < 0 ? `${text} before start` : `${text} after start`;
};

// Compact axis label: "-2h", "+30m", "+1h 30m"
const shortOffset = (minutes) => {
  const m = Math.round(minutes);
  if (m === 0) return '0';
  const abs = Math.abs(m);
  const text = abs >= 60 ? `${Math.floor(abs / 60)}h${abs % 60 ? ` ${abs % 60}m` : ''}` : `${abs}m`;
  return `${m < 0 ? '−' : '+'}${text}`;
};

// Games overlaid on one small chart, x = minutes from game start
const CompareSpark = ({ lines, target, unit }) => {
  const [hover, setHover] = useState(null);
  const all = lines.flatMap((l) => l.points);
  if (all.length < 2) return <div className="cx-spark-empty" style={{ height: 70 }}>Not enough data</div>;
  const x0 = Math.min(...all.map((p) => p.t));
  const x1 = Math.max(...all.map((p) => p.t));
  const span = Math.max(1, x1 - x0);
  const maxY = Math.max(target || 0, ...all.map((p) => p.y)) * 1.15 || 1;
  const x = (t) => ((t - x0) / span) * 100;
  const y = (v) => 100 - (v / maxY) * 100;

  const nearest = (points, t) =>
    points.reduce((best, p) => (!best || Math.abs(p.t - t) < Math.abs(best.t - t) ? p : best), null);

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const t = x0 + Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)) * span;
    setHover(t);
  };

  const startX = x0 <= 0 && x1 >= 0 ? x(0) : null;

  return (
    <div className="cx-cs-spark" onPointerMove={handleMove} onPointerLeave={() => setHover(null)}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        {target !== undefined && (
          <line x1="0" x2="100" y1={y(target)} y2={y(target)} className="cx-spark-target" vectorEffect="non-scaling-stroke" />
        )}
        {startX !== null && (
          <line x1={startX} x2={startX} y1="0" y2="100" className="cx-cs-start" vectorEffect="non-scaling-stroke" />
        )}
        {lines.map((l) => (
          <polyline
            key={l.key}
            points={l.points.map((p) => `${x(p.t).toFixed(2)},${y(p.y).toFixed(2)}`).join(' ')}
            fill="none"
            stroke={l.color}
            strokeWidth="1.75"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
      {hover !== null && (
        <>
          <div className="cx-spark-guide" style={{ left: `${x(hover)}%` }} />
          <div className={`cx-spark-tip cx-cs-tip${x(hover) > 55 ? ' left' : ''}`} style={{ left: `${x(hover)}%` }}>
            <span className="cx-spark-tip-time">{offsetLabel(hover)}</span>
            {lines.map((l) => {
              const p = nearest(l.points, hover);
              return p ? (
                <span key={l.key} className="cx-cs-tip-row">
                  <span className="cx-ov-swatch" style={{ background: l.color }} />
                  {l.label}: <strong className={p.o ? 'overflow' : ''}>{Math.round(p.y)}{p.o ? '+' : ''} {unit}</strong>
                </span>
              ) : null;
            })}
          </div>
        </>
      )}
      <div className="cx-sc-axis">
        <span>{shortOffset(x0)}</span>
        {/* Only label the start marker when it isn't crowding the edge labels */}
        {startX !== null && startX > 25 && startX < 75 && (
          <span className="cx-cs-axis-start" style={{ left: `${startX}%` }}>Game start</span>
        )}
        <span>{shortOffset(x1)}</span>
      </div>
    </div>
  );
};

const SectionCompareCards = ({ sections, games, isCountMode, activeSection, onSelectSection, authCtx, currentLocation, extension }) => {
  const unit = isCountMode ? 'people' : 'min';
  const statusOf = (v, overflow) =>
    isCountMode ? getPeopleCountStatus(v, COUNT_SECTION_LOW, COUNT_SECTION_HIGH) : getQueueTimeStatus(v, overflow);
  const withData = games.filter((g) => g.hasData);

  const cards = sections
    .map((id) => {
      const rows = withData.map((g) => ({ game: g, m: g.sections?.[id] })).filter((r) => r.m?.hasData);
      const avg = rows.length ? Math.round(rows.reduce((s, r) => s + r.m.avg, 0) / rows.length) : 0;
      const overGames = rows.filter((r) => statusOf(r.m.avg) === 'bad').length;
      const overflowMinutes = rows.reduce((s, r) => s + (r.m.overflow || 0), 0);
      return { id, rows, avg, overGames, overflowMinutes };
    })
    .filter((c) => c.rows.length)
    .sort((a, b) => b.overflowMinutes - a.overflowMinutes || b.avg - a.avg);

  // One scale for every bar, so cards compare with each other
  const barMax = Math.max(isCountMode ? COUNT_SECTION_HIGH : QUEUE_TIME_GOOD_MAX * 1.5, ...cards.flatMap((c) => c.rows.map((r) => r.m.avg)));

  return (
    <div className="cx-cs-grid">
      {cards.map((c) => {
        const status = statusOf(c.avg);
        const active = activeSection === c.id;
        return (
          <div
            key={c.id}
            className={`cx-cs-card${active ? ' active' : ''}`}
            onClick={() => onSelectSection(c.id)}
          >
            <div className="cx-cs-head">
              <button
                type="button"
                role="tab"
                aria-selected={active}
                className={`cx-cs-title${active ? ' active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectSection(c.id);
                }}
              >
                Section {c.id}
              </button>
              {c.overflowMinutes > 0 ? (
                <OverflowPill label={`Overflow ${formatDuration(c.overflowMinutes)}`} />
              ) : (
                <StatusPill
                  status={c.overGames > 0 ? 'bad' : 'good'}
                  label={c.overGames > 0 ? `${isCountMode ? 'Busy' : 'Over target'} in ${c.overGames} of ${c.rows.length}` : isCountMode ? 'Light' : 'On target'}
                />
              )}
            </div>

            <div className="cx-cs-headline">
              <span className="cx-cs-big" style={{ color: STATUS_COLORS[status] }}>{c.avg}</span>
              <span className="cx-cs-big-unit">{unit} avg across games</span>
            </div>

            <div className="cx-cs-rows">
              <div className="cx-cs-rows-label">{isCountMode ? 'Average count by game' : 'Average queue by game'}</div>
              {c.rows.map(({ game, m }) => {
                const rowStatus = statusOf(m.avg);
                return (
                  <div key={game.date} className="cx-cs-row cx-fade-in" title={`Peak ${m.peak}${m.peakOverflow ? '+' : ''} ${unit}${m.overflow ? ` · Overflow ${formatDuration(m.overflow)}` : ''}`}>
                    <span className="cx-cs-game">
                      <span className="cx-ov-swatch" style={{ background: game.color }} />
                      {moment(game.date).format('MMM D')}
                    </span>
                    <span className="cx-cs-bar">
                      <span
                        className="cx-cs-bar-fill"
                        style={{ width: `${Math.max(3, (m.avg / barMax) * 100)}%`, background: game.color }}
                      />
                    </span>
                    <span className="cx-cs-value" style={{ color: STATUS_COLORS[rowStatus] }}>
                      {m.avg}<small>{isCountMode ? ' ppl' : ' min'}</small>
                    </span>
                    <span className="cx-cs-flag">
                      {m.overflow > 0 ? <Icon name="exclamation-triangle-fill" /> : null}
                    </span>
                  </div>
                );
              })}
            </div>

            <CompareSpark
              lines={c.rows.map(({ game, m }) => ({
                key: game.date,
                label: moment(game.date).format('MMM D'),
                color: game.color,
                points: m.offsetSeries || [],
              }))}
              target={isCountMode ? undefined : QUEUE_TIME_GOOD_MAX}
              unit={unit}
            />

            {/* Venue-specific additions for this section (extensions/registry.js) */}
            <Slot
              name="overview.sectionCard"
              extension={extension}
              authCtx={authCtx}
              currentLocation={currentLocation}
              sectionId={c.id}
              games={c.rows.map((r) => r.game)}
            />

            <div className="cx-cs-foot">
              {active ? 'Showing charts below' : 'View charts'}
              <Icon name={active ? 'chevron-down' : 'arrow-right'} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SectionCompareCards;
